import React from 'react';
import { Tag } from 'antd';
import { FilePdfOutlined, FileTextOutlined } from '@ant-design/icons';
import {
  MODE_OF_DELIVERY_LABELS,
  PAYMENT_STATUS_LABELS,
  TRANSACTION_TYPE_LABELS,
} from '../../../../services/purchaseDealSheetService';
import { PAYMENT_STATUS_COLORS } from './purchaseDealSheet.constants';
import {
  columnSortOrder,
  formatCurrency,
  formatDate,
  formatDateTime,
} from './purchaseDealSheet.helpers';

export function getPurchaseDealSheetColumns({ sortState }) {
  return [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 72,
      sorter: true,
      sortOrder: columnSortOrder(sortState, 'id'),
      render: (id) => (
        <span className="font-mono text-xs text-gray-600">{id ?? '—'}</span>
      ),
    },
    {
      title: 'Transaction Date',
      dataIndex: 'transaction_date',
      key: 'transaction_date',
      width: 160,
      sorter: true,
      sortOrder: columnSortOrder(sortState, 'transaction_date'),
      render: (value) => formatDate(value),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 150,
      render: (text) => (
        <span className="whitespace-nowrap font-medium text-gray-800">{text || '—'}</span>
      ),
    },
    {
      title: 'Security / ISIN',
      key: 'security',
      width: 220,
      render: (_, row) => (
        <div className="leading-tight">
          <div className="whitespace-nowrap font-medium text-gray-800">
            {row.security_name || '—'}
          </div>
          {row.isin ? (
            <div className="whitespace-nowrap text-xs text-gray-500 font-mono mt-0.5">
              {row.isin}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Qty × Price',
      key: 'qty_price',
      width: 140,
      align: 'right',
      render: (_, row) => (
        <span className="whitespace-nowrap">
          {row.quantity ?? '—'} × {formatCurrency(row.price_per_share)}
        </span>
      ),
    },
    {
      title: 'Total Payable',
      dataIndex: 'total_payable',
      key: 'total_payable',
      width: 140,
      align: 'right',
      render: (value) => (
        <span className="whitespace-nowrap font-semibold">{formatCurrency(value)}</span>
      ),
    },
    {
      title: 'Paid',
      dataIndex: 'total_paid_amount',
      key: 'total_paid_amount',
      width: 120,
      align: 'right',
      render: (value) => (
        <span className="whitespace-nowrap">{formatCurrency(value)}</span>
      ),
    },
    {
      title: 'Payment Status',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 140,
      render: (status) => (
        <Tag color={PAYMENT_STATUS_COLORS[status] || 'default'} className="m-0">
          {PAYMENT_STATUS_LABELS[status] || status || '—'}
        </Tag>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'transaction_type',
      key: 'transaction_type',
      width: 180,
      render: (type, row) => (
        <span className="whitespace-nowrap">
          {row.transaction_type_label || TRANSACTION_TYPE_LABELS[type] || type || '—'}
        </span>
      ),
    },
    {
      title: 'Delivery',
      dataIndex: 'mode_of_delivery',
      key: 'mode_of_delivery',
      width: 100,
      render: (mode, row) => (
        <span className="whitespace-nowrap">
          {row.mode_of_delivery_label || MODE_OF_DELIVERY_LABELS[mode] || mode || '—'}
        </span>
      ),
    },
    {
      title: 'Purchase Deal Sheet',
      key: 'pdf',
      width: 140,
      align: 'center',
      render: (_, row) =>
        row.pdf_url ? (
          <a
            href={row.pdf_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex text-blue-600 hover:text-blue-700"
            aria-label="View purchase deal sheet"
          >
            <FilePdfOutlined />
          </a>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: 'Supplier Sheet',
      key: 'supplier_deal_sheet',
      width: 72,
      align: 'center',
      render: (_, row) =>
        row.supplier_deal_sheet_url ? (
          <a
            href={row.supplier_deal_sheet_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex text-blue-600 hover:text-blue-700"
            aria-label="View supplier deal sheet"
          >
            <FileTextOutlined />
          </a>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      sorter: true,
      sortOrder: columnSortOrder(sortState, 'created_at'),
      render: (value) => (
        <span className="whitespace-nowrap">{formatDateTime(value)}</span>
      ),
    },
  ];
}
